import React from 'react';

import { postWebUiApi } from '../../api';
import { WebUiAlert } from '../../components/alert';
import { WebUiButton } from '../../components/button';
import { WebUiInput } from '../../components/input';
import WebUiPageWrapper from '../../components/page-wrapper';
import { useWebUiGlobalContext } from '../../context/global/use-global-context';

type Bridge = {
  vlan_id: number;
  ip?: string;
  sm?: string;
  gw?: string;
  forward?: boolean;
  proxy_arp?: boolean;
  dhcp_client?: boolean;
  dhcp_provision?: boolean;
  dhcp_filter?: boolean;
};

export default function WebUiSettingsInterfacesPage() {
  const { currentUser } = useWebUiGlobalContext();
  const readonly = currentUser?.userLevel === 1;
  const [cfg, setCfg] = React.useState<Bridge[]>([]);
  const [running, setRunning] = React.useState<Bridge[]>([]);
  const [error, setError] = React.useState<string | null>(null);
  const [message, setMessage] = React.useState<string | null>(null);

  const reload = React.useCallback(async () => {
    const [bridges, current] = await Promise.all([
      postWebUiApi<Bridge[]>({
        service: 'vlan',
        access: 'get',
        command: 'get bridges',
      }),
      postWebUiApi<Bridge[]>({
        service: 'vlan',
        access: 'get',
        command: 'get current bridge config',
      }),
    ]);
    setCfg(bridges ?? []);
    setRunning(current ?? []);
  }, []);

  React.useEffect(() => {
    reload().catch((err) => setError((err as Error).message));
  }, [reload]);

  const patch = React.useCallback((vid: number, p: Partial<Bridge>) => {
    setCfg((prev) => prev.map((b) => (b.vlan_id === vid ? { ...b, ...p } : b)));
  }, []);

  const runningByVid = React.useMemo(
    () => new Map(running.map((i) => [i.vlan_id, i])),
    [running],
  );

  return (
    <WebUiPageWrapper titleList={['Settings', 'Management Interfaces']}>
      {message ? <WebUiAlert type="success" message={message} /> : null}
      {error ? <WebUiAlert type="error" message={error} /> : null}
      <div className="flex w-full flex-col gap-2">
        <h4 className="font-bold">Management Interfaces</h4>
        <table className="webui-table w-full">
          <thead>
            <tr className="webui-table-row-head">
              <th rowSpan={2}>Interface</th>
              <th colSpan={3}>Settings</th>
              <th colSpan={2}>NAT</th>
              <th colSpan={3}>DHCP Client</th>
              <th colSpan={3}>Status</th>
            </tr>
            <tr className="webui-table-row-head">
              <th>IP</th>
              <th>Netmask</th>
              <th>Gateway</th>
              <th>Forward</th>
              <th>Proxy ARP</th>
              <th>Operation</th>
              <th>Provision</th>
              <th>Filter</th>
              <th>IP</th>
              <th>Netmask</th>
              <th>Gateway</th>
            </tr>
          </thead>
          <tbody>
            {cfg.map((item) => {
              const r = runningByVid.get(item.vlan_id);
              const name =
                item.vlan_id === 0 ? 'Lan' : `VLAN_ID${item.vlan_id}`;
              return (
                <tr key={item.vlan_id} className="webui-table-row-content">
                  <td>{name}</td>
                  <td>
                    <WebUiInput
                      inputSize="sm"
                      disabled={readonly}
                      value={item.ip ?? ''}
                      onChange={(e) =>
                        patch(item.vlan_id, { ip: e.target.value })
                      }
                    />
                  </td>
                  <td>
                    <WebUiInput
                      inputSize="sm"
                      disabled={readonly}
                      value={item.sm ?? ''}
                      onChange={(e) =>
                        patch(item.vlan_id, { sm: e.target.value })
                      }
                    />
                  </td>
                  <td>
                    <WebUiInput
                      inputSize="sm"
                      disabled={readonly}
                      value={item.gw ?? ''}
                      onChange={(e) =>
                        patch(item.vlan_id, { gw: e.target.value })
                      }
                    />
                  </td>
                  <td className="text-center">
                    <WebUiInput
                      type="checkbox"
                      disabled={readonly}
                      checked={Boolean(item.forward)}
                      onChange={(e) =>
                        patch(item.vlan_id, { forward: e.target.checked })
                      }
                    />
                  </td>
                  <td className="text-center">
                    <WebUiInput
                      type="checkbox"
                      disabled={readonly}
                      checked={Boolean(item.proxy_arp)}
                      onChange={(e) =>
                        patch(item.vlan_id, { proxy_arp: e.target.checked })
                      }
                    />
                  </td>
                  <td className="text-center">
                    <WebUiInput
                      type="checkbox"
                      disabled={readonly}
                      checked={Boolean(item.dhcp_client)}
                      onChange={(e) =>
                        patch(item.vlan_id, { dhcp_client: e.target.checked })
                      }
                    />
                  </td>
                  <td className="text-center">
                    <WebUiInput
                      type="checkbox"
                      disabled={readonly}
                      checked={Boolean(item.dhcp_provision)}
                      onChange={(e) =>
                        patch(item.vlan_id, {
                          dhcp_provision: e.target.checked,
                        })
                      }
                    />
                  </td>
                  <td className="text-center">
                    <WebUiInput
                      type="checkbox"
                      disabled={readonly}
                      checked={Boolean(item.dhcp_filter)}
                      onChange={(e) =>
                        patch(item.vlan_id, { dhcp_filter: e.target.checked })
                      }
                    />
                  </td>
                  <td className="text-center">{r?.ip ?? ''}</td>
                  <td className="text-center">{r?.sm ?? ''}</td>
                  <td className="text-center">{r?.gw ?? ''}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
        <div>
          <WebUiButton
            disabled={readonly}
            onClick={async () => {
              try {
                await postWebUiApi({
                  service: 'vlan',
                  access: 'set',
                  command: 'set bridges',
                  parameters: cfg,
                });
                setMessage('Management interfaces updated.');
                setError(null);
                await reload();
              } catch (err) {
                setError((err as Error).message);
              }
            }}
          >
            Set Management Interfaces
          </WebUiButton>
        </div>
      </div>
    </WebUiPageWrapper>
  );
}
